import React, { useEffect, useRef, useState } from 'react';
import Spinner from '../shared/Spinner';
import LoadFailure from '../shared/LoadFailure';
import { apiFetch } from '../shared/api';

// The endpoint returns a single candidate today (`data` is an object or null),
// but normalising to a list here means a provider that starts returning several
// needs no change on this side - the picker already renders and selects N.
export function toCandidateList(data) {
    if (!data) {
        return [];
    }

    return (Array.isArray(data) ? data : [data]).filter((entry) => entry && entry.url);
}

// `currentUrl` is the logo already assigned to the record (edit page). When set,
// it is shown as the selected preview so the picker reflects the currently
// assigned logo instead of defaulting to the freshly auto-resolved candidate.
// It is null on the add page, where there is nothing assigned yet.
export default function LogoPicker({ endpoints, domain, businessName, googleImageSearchBase, currentUrl = null, value, onChange }) {
    const [status, setStatus] = useState(currentUrl ? 'candidate' : 'loading');
    const [candidates, setCandidates] = useState(currentUrl ? [{ url: currentUrl, source: 'current' }] : []);
    const [selectedUrl, setSelectedUrl] = useState(currentUrl);
    const [searchAttempt, setSearchAttempt] = useState(0);
    const [file, setFile] = useState(value?.file || null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const previewUrlRef = useRef(null);
    const fileRef = useRef(file);
    fileRef.current = file;
    // The query string only carries a cache-buster, so it must not re-fire the effect below.
    const currentPath = currentUrl ? currentUrl.split('?')[0] : null;

    // Keeps the preview on the freshest cache-busted URL without re-reporting to the parent.
    useEffect(() => {
        if (currentUrl) {
            setCandidates([{ url: currentUrl, source: 'current' }]);
            setSelectedUrl(currentUrl);
        }
    }, [currentUrl]);

    useEffect(() => {
        // Edit page: a logo is already assigned. Show only that logo as the preview
        // and do NOT auto-resolve a candidate (doing both showed a duplicate). The
        // user overrides it with an upload or the Google image search button.
        if (currentUrl) {
            setStatus('candidate');
            setCandidates([{ url: currentUrl, source: 'current' }]);
            setSelectedUrl(currentUrl);
            if (!fileRef.current) {
                onChange({ candidateUrl: currentUrl, file: null });
            }
            return undefined;
        }

        if (!domain) {
            setStatus('none');
            setCandidates([]);
            setSelectedUrl(null);
            return undefined;
        }

        let cancelled = false;
        setStatus('loading');

        apiFetch(`${endpoints.logoFor}?domain=${encodeURIComponent(domain)}`).then(({ status: httpStatus, json }) => {
            if (cancelled) {
                return;
            }

            if (httpStatus >= 200 && httpStatus < 300) {
                const fetched = toCandidateList(json?.data);
                const first = fetched.length > 0 ? fetched[0].url : null;
                setCandidates(fetched);
                setSelectedUrl(first);
                setStatus(fetched.length > 0 ? 'candidate' : 'none');
                if (!fileRef.current) {
                    onChange({ candidateUrl: first, file: null });
                }
            } else {
                console.error(`Logo search failed with status ${httpStatus}`);
                setStatus('unavailable');
            }
        }).catch((error) => {
            console.error(error);
            if (!cancelled) {
                setStatus('unavailable');
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domain, endpoints.logoFor, currentPath, searchAttempt]);

    useEffect(() => () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }
    }, []);

    const handleSelectCandidate = (url) => {
        setSelectedUrl(url);
        if (!fileRef.current) {
            onChange({ candidateUrl: url, file: null });
        }
    };

    const handleUpload = (event) => {
        const selected = event.target.files && event.target.files[0];
        if (!selected) {
            return;
        }

        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
        }

        const objectUrl = URL.createObjectURL(selected);
        previewUrlRef.current = objectUrl;
        setPreviewUrl(objectUrl);
        setFile(selected);
        onChange({ candidateUrl: selectedUrl, file: selected });
    };

    const handleRemoveUpload = () => {
        if (previewUrlRef.current) {
            URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = null;
        }
        setPreviewUrl(null);
        setFile(null);
        onChange({ candidateUrl: selectedUrl, file: null });
    };

    const uploadActive = Boolean(file && previewUrl);
    const hasCandidates = status === 'candidate' && candidates.length > 0;
    const showPlaceholderMessage = !uploadActive && !hasCandidates;

    return (
        <div className="form-group demo-add-logo-picker" data-testid="demo-add-logo">
            <label>{__('Logo')}</label>

            <div className="row">
                {uploadActive &&
                    <div className="col-4 col-sm-3 mb-2">
                        <div className="card text-center h-100 demo-add-logo-card is-selected">
                            <div className="card-body d-flex align-items-center justify-content-center p-2">
                                <img src={previewUrl} alt={businessName} className="demo-add-logo-preview" />
                            </div>
                        </div>
                    </div>
                }

                {!uploadActive && hasCandidates && candidates.map((entry) => {
                    const isSelected = entry.url === selectedUrl;

                    return (
                        <div className="col-4 col-sm-3 mb-2" key={entry.url}>
                            <button
                                type="button"
                                className={`card text-center h-100 demo-add-logo-card${isSelected ? ' is-selected' : ''}`}
                                data-testid="demo-add-logo-candidate"
                                aria-pressed={isSelected}
                                aria-label={__('Select this logo')}
                                onClick={() => handleSelectCandidate(entry.url)}
                            >
                                <div className="card-body d-flex align-items-center justify-content-center p-2">
                                    <img src={entry.url} alt={businessName} className="demo-add-logo-preview" />
                                </div>
                            </button>
                        </div>
                    );
                })}

                {status === 'loading' && showPlaceholderMessage &&
                    <div className="col-12">
                        <Spinner block label={__('Looking for a logo')} />
                        <p className="text-center">{__('Looking for a logo...')}</p>
                    </div>
                }
                {status === 'none' && showPlaceholderMessage && <div className="col-12"><p>{__('No logo found for this business.')}</p></div>}
                {status === 'unavailable' && showPlaceholderMessage &&
                    <div className="col-12 mb-2">
                        <LoadFailure
                            message={__('Logo search is temporarily unavailable.')}
                            onRetry={() => setSearchAttempt((value) => value + 1)}
                        />
                    </div>
                }
            </div>

            {/* Search link and upload sit side by side; the row wraps on narrow
                viewports rather than squeezing both controls. */}
            <div className="demo-add-logo-controls">
                <a
                    href={`${googleImageSearchBase}${encodeURIComponent(`${businessName} logo`)}`}
                    target="_blank"
                    rel="noopener"
                    className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                >
                    <i className="fas fa-search mr-2" aria-hidden="true" />
                    {__('Do not see the correct company logo? Search for one online')}
                </a>

                <div className="demo-add-logo-actions">
                    <div className="custom-file">
                        <input
                            type="file"
                            accept="image/*"
                            aria-label={__('Upload a logo')}
                            className="demo-add-logo-file"
                            onChange={handleUpload}
                        />
                    </div>

                    {file &&
                        <button
                            type="button"
                            className="btn btn-link demo-add-logo-remove"
                            onClick={handleRemoveUpload}
                            title={__('Remove upload')}
                            aria-label={__('Remove upload')}
                        >
                            <i className="fas fa-trash" aria-hidden="true" />
                        </button>
                    }
                </div>
            </div>
        </div>
    );
}
